import ContentContainer from '@/features/shared/ContentContainer';
import FourFiveColumnContent from '@/features/shared/FourFiveColumnContent';
import { MuiContainer, MuiTypography } from '@/features/shared/material-ui';
import { SpacingSizes, Theme } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import { FeaturedContentProps } from '../FeaturedContent.interface';
import { ColumnCard } from '../shared/ColumnCard';
import { CtaCard } from '../shared/CtaCard';
import { CustomerCard } from '../shared/CustomerCard';
import useStyles from './RowType.styles';

const getFeatureContent = ({ contentList }: ContentListType): CardType => {
  const card = contentList && contentList.find((item: any) => item.featuredContent);

  return card as CardType;
};

const RowTypeCard = ({ card, styles, categoryOnTop, ...props }: any) => {
  if (/image/.test(card.variant)) {
    return <CtaCard cardImage={card.image} {...styles} {...props} />;
  }

  return <CustomerCard {...card} {...styles} {...props} categoryOnTop={categoryOnTop} />;
};

const RowType = ({
  curatedList,
  contentButtons,
  cardColor,
  theme = Theme.light,
  presentation,
  cardTheme,
  template,
}: FeaturedContentProps) => {
  const classes = useStyles({});
  const { contentList = [], listSuperheading, listTitle } = curatedList || {};
  const featuredContentCard = useMemo(() => getFeatureContent({ contentList }), [contentList]);
  const filterList = useMemo(
    () =>
      featuredContentCard && contentList
        ? contentList.filter((item: any) => item?.cardHeading !== featuredContentCard?.cardHeading)
        : contentList,
    [contentList, featuredContentCard],
  );
  const isRowType2 = useMemo(() => (template ? /row-2$/i.test(template) : false), [template]);

  const { topSpacing, bottomSpacing, topBackgroundColor, bottomBackgroundColor } =
    presentation || {};

  const styles = {
    cardColor,
    cardTheme,
    theme,
  };

  const hasHeadings = listSuperheading || listTitle;
  const hasTopContent = hasHeadings || featuredContentCard;
  const headerClassName = clsx(
    classes.headerCard,
    `${theme}-theme`,
    featuredContentCard && 'with-featured',
  );

  return (
    <>
      {hasTopContent && (
        <ContentContainer
          topSpacing={topSpacing}
          bottomSpacing={SpacingSizes.small}
          topBackgroundColor={topBackgroundColor}
          bottomBackgroundColor={topBackgroundColor}
        >
          <MuiContainer>
            {contentButtons?.curatedList}
            {(listSuperheading || listTitle) && (
              <div className={headerClassName}>
                {listSuperheading && (
                  <MuiTypography
                    variant="overline"
                    component="div"
                    className={classes.superHeading}
                  >
                    {listSuperheading}
                  </MuiTypography>
                )}
                {listTitle && (
                  <MuiTypography variant="h4" className={classes.title}>
                    {listTitle}
                  </MuiTypography>
                )}
              </div>
            )}
            {featuredContentCard && (
              <ColumnCard
                {...featuredContentCard}
                cardColor={cardColor}
                theme={theme}
                isFeaturedContent
              />
            )}
          </MuiContainer>
        </ContentContainer>
      )}

      {filterList.length > 0 && (
        <ContentContainer
          bottomSpacing={bottomSpacing}
          topSpacing={SpacingSizes.small}
          topBackgroundColor={topBackgroundColor}
          bottomBackgroundColor={bottomBackgroundColor}
        >
          {!hasTopContent && contentButtons?.curatedList}
          <MuiContainer className={featuredContentCard ? 'medium' : 'large'}>
            <FourFiveColumnContent
              columnCount={featuredContentCard ? 2 : 3}
              className={classes.contentCards}
              columns={filterList.map((card: any, idx: number) => {
                return (
                  <RowTypeCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={`card_${idx}`}
                    card={card}
                    styles={styles}
                    categoryOnTop={isRowType2}
                    className={classes.card}
                  />
                );
              })}
            />
          </MuiContainer>
        </ContentContainer>
      )}
    </>
  );
};

export default memo(RowType);
